import { defaultBlockRegistry } from '../renderer/block-registry.gen.js';
/**
 * Fields never GENERATED — but still listed (generatable:false) so a virgin-start run can STRIP the
 * theme's demo pixels out of them (founder 2026-08-21: «configuration vierge, aucune photo»): a logo
 * would come back lettered with gibberish, badges are trust furniture, and an AI avatar is a fake
 * face on a fake review.
 */
const NON_GENERATABLE_FIELD = /logo|favicon|badge|avatar/i;
/** Sections listed but never generated into — their photos are template pixels to strip, not to remake. */
const NON_GENERATABLE_SECTIONS = new Set([
    'testimonials',
    'facebook-testimonials',
    'logo-list',
    'insta-stories',
]);
/**
 * Sections NEVER LISTED AT ALL: they render the seller's REAL product (gallery, buy box) — their
 * images are the seller's own photos, neither template pixels to strip nor slots to generate. The
 * 2026-08-20 run burnt 6 of its 7-image budget writing AI decor into the main-product gallery.
 */
const HANDS_OFF_SECTIONS = new Set(['main-product', 'cod-buybox']);
const MAX_BLOCK_DEPTH = 32;
/** Context is a steer, not a brief — one card title, not the card's whole paragraph stack. */
const MAX_CONTEXT_CHARS = 160;
function imageFields(fields) {
    const out = [];
    for (const f of fields ?? []) {
        if (!f || f.type !== 'image')
            continue;
        // `aiWritable: false` is the schema author's hands-off switch — not listed, so neither
        // generated into nor stripped.
        if (f.aiWritable === false)
            continue;
        out.push({
            fieldId: f.id,
            label: f.label ?? f.id,
            generatable: !NON_GENERATABLE_FIELD.test(f.id) && !NON_GENERATABLE_FIELD.test(f.label ?? ''),
        });
    }
    return out;
}
/** The node's own short strings — plain-string settings only, richtext and long prose skipped. */
function contextOf(settings) {
    if (!settings)
        return '';
    const parts = [];
    for (const value of Object.values(settings)) {
        if (typeof value !== 'string')
            continue;
        const t = value.trim();
        // Skip empties, URLs/anchors and anything paragraph-sized — a steer, not a brief.
        if (!t || t.startsWith('http') || t.startsWith('#') || t.length > 80)
            continue;
        parts.push(t);
        if (parts.join('\n').length > MAX_CONTEXT_CHARS)
            break;
    }
    return parts.join('\n').slice(0, MAX_CONTEXT_CHARS);
}
function pushImageSlots(fields, sectionType, nodeType, settings, path, acc) {
    const sectionVeto = NON_GENERATABLE_SECTIONS.has(sectionType);
    for (const f of fields) {
        const current = settings?.[f.fieldId];
        acc.push({
            path,
            fieldId: f.fieldId,
            sectionType,
            nodeType,
            label: f.label,
            context: contextOf(settings),
            hasCurrentImage: typeof current === 'object' &&
                current !== null &&
                typeof current.url === 'string' &&
                current.url !== '',
            generatable: f.generatable && !sectionVeto,
        });
    }
}
function walkBlocks(blocks, parentPath, sectionType, inlineChildren, globalBlocks, acc, depth) {
    if (!Array.isArray(blocks) || depth > MAX_BLOCK_DEPTH)
        return;
    for (const block of blocks) {
        if (!block || typeof block !== 'object' || typeof block.id !== 'string')
            continue;
        const path = [...parentPath, block.id];
        // Same contextual resolution as the text surface: a direct child prefers the section's inline
        // definition (six sections each declare their own `column`), deeper nodes the global registry.
        const fields = depth === 0
            ? (inlineChildren.get(block.type) ?? globalBlocks.get(block.type) ?? [])
            : (globalBlocks.get(block.type) ?? inlineChildren.get(block.type) ?? []);
        pushImageSlots(fields, sectionType, block.type, block.settings, path, acc);
        walkBlocks(block.blocks, path, sectionType, inlineChildren, globalBlocks, acc, depth + 1);
    }
}
/** Every AI-writable image slot in the document, sections top to bottom, blocks depth-first. */
export function collectImageSlots(document, sectionRegistry, blockRegistry = defaultBlockRegistry) {
    const acc = [];
    try {
        const globalBlocks = new Map();
        for (const [type, def] of Object.entries(blockRegistry)) {
            globalBlocks.set(type, imageFields(def.schema.fields));
        }
        const sections = document?.sections;
        if (!Array.isArray(sections))
            return acc;
        for (const section of sections) {
            if (!section || typeof section !== 'object' || typeof section.id !== 'string')
                continue;
            if (section.disabled)
                continue;
            if (HANDS_OFF_SECTIONS.has(section.type))
                continue;
            const schema = sectionRegistry[section.type]?.schema;
            const inlineChildren = new Map();
            for (const child of schema?.blocks ?? []) {
                inlineChildren.set(child.type, imageFields(child.fields));
            }
            const path = [section.id];
            pushImageSlots(imageFields(schema?.fields), section.type, section.type, section.settings, path, acc);
            walkBlocks(section.blocks, path, section.type, inlineChildren, globalBlocks, acc, 0);
        }
    }
    catch {
        return acc;
    }
    return acc;
}
//# sourceMappingURL=imageSlots.js.map